/**
 * Celebration animation component for Props.
 * Uses canvas-confetti for particle effects with value-specific colors.
 * Respects prefers-reduced-motion.
 */

import confetti from "canvas-confetti";
import type { CompanyValue } from "@/types/recognition";

const REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Hex color → confetti-compatible hex string */
function hexToConfettiColor(hex: string): string {
  return hex.startsWith("#") ? hex : `#${hex}`;
}

/**
 * Tier 1 — Every props submission.
 * Short burst of ~30 value-colored particles.
 */
export function triggerCelebration(value?: CompanyValue) {
  if (REDUCED_MOTION) return;

  const color = value?.color ?? "#F97316";

  confetti({
    particleCount: 30,
    spread: 60,
    origin: { y: 0.7 },
    colors: [hexToConfettiColor(color), "#ffffff", "#fbbf24"],
    gravity: 1.2,
    scalar: 0.9,
    ticks: 120,
    disableForReducedMotion: true,
  });
}

/**
 * Tier 2 — Milestone celebrations (5th, 10th, 25th).
 * Larger burst with mixed value colors.
 */
export function triggerMilestoneCelebration() {
  if (REDUCED_MOTION) return;

  const valueColors = ["#EAB308", "#3B82F6", "#F59E0B", "#EF4444", "#F97316"];

  confetti({
    particleCount: 80,
    spread: 100,
    origin: { y: 0.6 },
    colors: valueColors,
    gravity: 1.0,
    scalar: 1.1,
    ticks: 180,
    disableForReducedMotion: true,
  });
}
